import React, { useState } from 'react';
import { Plus, Check, AlertCircle } from 'lucide-react';
import * as AssessmentService from '@services/AssessmentService';

type Entity = 'participant' | 'reportee' | 'manager';
type QuestionType = 'rating' | 'text';

interface Create360QuestionProps {
  selectedModuleId: number;
  moduleName: string;
  onQuestionAdded: () => void;
  isDarkMode: boolean;
  /** Type-4 (270) only: allow authoring subjective (free-text) questions via the type switch. */
  allowSubjective?: boolean;
  /** Assessment-wide count of existing subjective (text) questions per entity, for the 3/entity cap. */
  subjectiveCounts?: Record<Entity, number>;
}

interface LikertOption {
  text: string;
  value: number;
}

interface Question360Form {
  questionText: string;
  applicableTo: Entity | null;
  options: LikertOption[];
}

const DEFAULT_LIKERT_OPTIONS: LikertOption[] = [
  { text: 'Strongly Agree', value: 1 },
  { text: 'Agree', value: 0.5 },
  { text: 'Neutral', value: 0 },
  { text: 'Disagree', value: -0.5 },
  { text: 'Strongly Disagree', value: -1 }
];

/** Max subjective (free-text) questions per entity across the whole assessment, irrespective of module. */
const MAX_SUBJECTIVE_PER_ENTITY = 3;

const Create360Question: React.FC<Create360QuestionProps> = ({
  selectedModuleId,
  moduleName,
  onQuestionAdded,
  isDarkMode,
  allowSubjective = false,
  subjectiveCounts = { participant: 0, reportee: 0, manager: 0 }
}) => {
  const [questionType, setQuestionType] = useState<QuestionType>('rating');
  const [questionForm, setQuestionForm] = useState<Question360Form>({
    questionText: '',
    applicableTo: null,
    options: [...DEFAULT_LIKERT_OPTIONS]
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isTextMode = allowSubjective && questionType === 'text';
  const subjectiveCountFor = (entity: Entity) => subjectiveCounts[entity] ?? 0;
  const atSubjectiveCap = questionForm.applicableTo != null
    && subjectiveCountFor(questionForm.applicableTo) >= MAX_SUBJECTIVE_PER_ENTITY;

  const resetForm = () => {
    setQuestionForm({
      questionText: '',
      applicableTo: null,
      options: [...DEFAULT_LIKERT_OPTIONS]
    });
  };

  const handleOptionTextChange = (index: number, text: string) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = { ...newOptions[index], text };
    setQuestionForm({ ...questionForm, options: newOptions });
  };

  const handleOptionValueChange = (index: number, value: string) => {
    const newOptions = [...questionForm.options];
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      newOptions[index] = { ...newOptions[index], value: numValue };
      setQuestionForm({ ...questionForm, options: newOptions });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!questionForm.questionText.trim()) {
      setError('Please enter a question');
      return;
    }

    if (!questionForm.applicableTo) {
      setError('Please select who should answer this question');
      return;
    }

    if (isTextMode) {
      // Subjective (free-text) question: capped at 3 per entity across the whole assessment.
      if (subjectiveCountFor(questionForm.applicableTo) >= MAX_SUBJECTIVE_PER_ENTITY) {
        setError(`You can add at most ${MAX_SUBJECTIVE_PER_ENTITY} open-ended questions for this entity.`);
        return;
      }
    } else {
      // Validate all options have text
      if (questionForm.options.some(opt => !opt.text.trim())) {
        setError('Please fill all 5 option texts');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const questionData: AssessmentService.CreateQuestionData = isTextMode
        ? {
            moduleId: selectedModuleId,
            text: questionForm.questionText,
            applicable_to: questionForm.applicableTo,
            question_type: 'subjective'
          }
        : {
            moduleId: selectedModuleId,
            text: questionForm.questionText,
            applicable_to: questionForm.applicableTo,
            options: questionForm.options.map(opt => ({
              text: opt.text,
              value: opt.value,
              isCorrect: false // No correct answer for 360 feedback
            }))
          };

      const response = await AssessmentService.createQuestion(questionData);

      if (response.success) {
        setSuccessMessage(
          isTextMode
            ? 'Open-ended question added successfully!'
            : 'Feedback question added successfully!'
        );
        resetForm();
        onQuestionAdded();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error?.message || 'Failed to create question');
      }
    } catch (err: any) {
      console.error('Error creating feedback question:', err);
      setError(err.message || 'Failed to create question');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <h2 className="section-title">Add Feedback Question</h2>
      <p className="section-description">
        Creating question for: <strong>{moduleName}</strong>
      </p>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <Check size={20} />
          <span>{successMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="question-form">
        {/* Applicable To Selection - Tab UI */}
        <div className="form-group">
          <label className="form-label">
            Who should answer this question? <span style={{ color: '#ef4444' }}>*</span>
          </label>

          {/* Tab Container */}
          <div className="applicable-to-tabs">
            {/* Participant Tab */}
            <button
              type="button"
              onClick={() => setQuestionForm({ ...questionForm, applicableTo: 'participant' })}
              className={`tab-button ${questionForm.applicableTo === 'participant' ? 'active' : ''}`}
            >
              <span className="tab-icon">👤</span>
              <span className="tab-label">
                Participant{isTextMode ? ` (${subjectiveCountFor('participant')}/${MAX_SUBJECTIVE_PER_ENTITY})` : ''}
              </span>
            </button>

            {/* Manager Tab */}
            <button
              type="button"
              onClick={() => setQuestionForm({ ...questionForm, applicableTo: 'manager' })}
              className={`tab-button ${questionForm.applicableTo === 'manager' ? 'active' : ''}`}
            >
              <span className="tab-icon">👔</span>
              <span className="tab-label">
                Manager{isTextMode ? ` (${subjectiveCountFor('manager')}/${MAX_SUBJECTIVE_PER_ENTITY})` : ''}
              </span>
            </button>

            {/* Reportee Tab */}
            <button
              type="button"
              onClick={() => setQuestionForm({ ...questionForm, applicableTo: 'reportee' })}
              className={`tab-button ${questionForm.applicableTo === 'reportee' ? 'active' : ''}`}
            >
              <span className="tab-icon">👥</span>
              <span className="tab-label">
                Team Member{isTextMode ? ` (${subjectiveCountFor('reportee')}/${MAX_SUBJECTIVE_PER_ENTITY})` : ''}
              </span>
            </button>
          </div>

          {/* Helper Text */}
          <p className="form-hint">
            {questionForm.applicableTo === 'participant' && '✓ This question will be answered by the participant during self-assessment'}
            {questionForm.applicableTo === 'manager' && '✓ This question will be answered by the participant\'s manager(s)'}
            {questionForm.applicableTo === 'reportee' && '✓ This question will be answered by the participant\'s team members'}
            {!questionForm.applicableTo && 'ℹ️ Select who should answer this question'}
          </p>
        </div>

        {/* Question Type Switch (270 only): Rating (Likert) vs Open-ended (free text) */}
        {allowSubjective && (
          <div className="form-group">
            <label className="form-label">Question type</label>
            <div className="question-type-switch">
              <button
                type="button"
                onClick={() => setQuestionType('rating')}
                className={`type-switch-button ${questionType === 'rating' ? 'active' : ''}`}
              >
                ⭐ Rating (Likert)
              </button>
              <button
                type="button"
                onClick={() => setQuestionType('text')}
                className={`type-switch-button ${questionType === 'text' ? 'active' : ''}`}
              >
                ✍️ Open-ended (text)
              </button>
            </div>
            <p className="form-hint">
              {questionType === 'rating'
                ? '✓ Scored 5-point Likert question (asked per module)'
                : `✓ Free-text question, not scored — up to ${MAX_SUBJECTIVE_PER_ENTITY} per entity for the whole assessment (any module)`}
            </p>
          </div>
        )}

        {/* Question Text */}
        <div className="form-group">
          <label className="form-label">Question Text</label>
          <textarea
            value={questionForm.questionText}
            onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
            className="form-textarea"
            rows={3}
            placeholder={isTextMode
              ? 'Enter an open-ended feedback question (e.g. What should this person start doing?)'
              : 'Enter your feedback question here...'}
            disabled={isTextMode && atSubjectiveCap}
            required
          />
        </div>

        {/* Likert Scale Options (Editable) — hidden for open-ended questions */}
        {!isTextMode && (
        <div className="options-section">
          <label className="form-label">Response Options (5-point Likert Scale)</label>
          <p className="form-hint" style={{ marginTop: '4px', marginBottom: '12px' }}>
            ℹ️ Pre-filled with standard values. You can customize the text and values if needed.
          </p>
          <div className="options-grid">
            {questionForm.options.map((option, index) => (
              <div key={index} className="option-group">
                <div className="option-header">
                  <span className="option-label">Option {index + 1}</span>
                  <span className="option-value-badge">Value: {option.value}</span>
                </div>
                <div className="option-input-wrapper">
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionTextChange(index, e.target.value)}
                    className="form-input"
                    placeholder={`Enter option ${index + 1} text`}
                    required
                  />
                  <input
                    type="number"
                    value={option.value}
                    onChange={(e) => handleOptionValueChange(index, e.target.value)}
                    className="form-input-value"
                    step="0.1"
                    placeholder="Value"
                    required
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        <button
          type="submit"
          className="btn-submit"
          disabled={submitting || (isTextMode && atSubjectiveCap)}
        >
          <Plus size={20} />
          <span>
            {isTextMode && atSubjectiveCap
              ? `Maximum ${MAX_SUBJECTIVE_PER_ENTITY} open-ended reached for this entity`
              : submitting
                ? 'Adding Question...'
                : isTextMode
                  ? 'Add Open-ended Question'
                  : 'Add Feedback Question'}
          </span>
        </button>
      </form>

      <style>{`
        .applicable-to-tabs {
          display: flex;
          gap: 8px;
          padding: 4px;
          background: ${isDarkMode ? '#1f2937' : '#f3f4f6'};
          border-radius: 12px;
          border: 1px solid ${isDarkMode ? '#374151' : '#e5e7eb'};
        }

        .tab-button {
          flex: 1;
          padding: 14px 16px;
          background: transparent;
          color: ${isDarkMode ? '#9ca3af' : '#6b7280'};
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .tab-button:hover:not(.active) {
          background: ${isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(102, 126, 234, 0.05)'};
        }

        .tab-button.active {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .tab-icon {
          font-size: 20px;
        }

        .tab-label {
          font-size: 13px;
          font-weight: 600;
          text-align: center;
        }

        .form-hint {
          margin-top: 8px;
          font-size: 13px;
          color: ${isDarkMode ? '#9ca3af' : '#6b7280'};
          font-style: italic;
        }

        .option-value-badge {
          font-size: 12px;
          font-weight: 600;
          color: #667eea;
          background: ${isDarkMode ? 'rgba(102, 126, 234, 0.15)' : 'rgba(102, 126, 234, 0.1)'};
          padding: 3px 10px;
          border-radius: 10px;
        }

        .form-input-value {
          width: 80px;
          padding: 12px;
          border: 1px solid ${isDarkMode ? '#333333' : '#e0e0e0'};
          border-radius: 8px;
          font-size: 14px;
          color: ${isDarkMode ? '#ffffff' : '#1a1a1a'};
          background: ${isDarkMode ? '#252525' : '#ffffff'};
          transition: all 0.3s ease;
          text-align: center;
          font-weight: 600;
        }

        .form-input-value:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .question-type-switch {
          display: flex;
          gap: 8px;
        }

        .type-switch-button {
          flex: 1;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid ${isDarkMode ? '#374151' : '#e5e7eb'};
          background: transparent;
          color: ${isDarkMode ? '#9ca3af' : '#6b7280'};
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .type-switch-button:hover:not(.active) {
          background: ${isDarkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(102, 126, 234, 0.05)'};
        }

        .type-switch-button.active {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border-color: #667eea;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
      `}</style>
    </div>
  );
};

export default Create360Question;
